import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import api from "../api/client";
import ProjectNav from "../components/ProjectNav";

const BASE = "http://127.0.0.1:8000";

type AgentStatus = "idle" | "queued" | "running" | "complete" | "failed";
type LogStatus = "success" | "warning" | "running" | "failed";

interface LogEntry {
  time: string;
  status: LogStatus;
  message: string;
  detail?: string;
}

function StatusIcon({ status }: { status: LogStatus }) {
  if (status === "success")
    return (
      <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  if (status === "warning")
    return (
      <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    );
  if (status === "running")
    return (
      <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    );
  return (
    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

const STAGE_LABELS: Record<string, string> = {
  data_agent_output: "EVM data fetched and analysed",
  final_report: "AI narrative report assembled",
  html_report: "HTML report rendered",
  failed: "Agent pipeline failed",
};

const AGENT_STAGE_PROGRESS: Record<string, string> = {
  data_agent_output: "Fetching and analysing EVM data...",
  final_report: "Assembling AI narrative report...",
  html_report: "Rendering HTML report...",
  failed: "Agent encountered an error",
};

export default function Processing() {
  const { projectId } = useParams();
  const { state } = useLocation() as any;
  const navigate = useNavigate();

  const name = localStorage.getItem("name") || "User";
  const role = localStorage.getItem("role") || "viewer";

  const [reportId, setReportId] = useState<string | null>(state?.reportId || null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus>("idle");
  const [agentRunId, setAgentRunId] = useState<string | null>(null);
  const [agentStage, setAgentStage] = useState<string>("");
  const [progress, setProgress] = useState(85);
  const [preparing, setPreparing] = useState(!!state?.processPayload);
  const [prepareError, setPrepareError] = useState("");
  const [extractionLogs, setExtractionLogs] = useState<LogEntry[]>([]);

  const prevStageRef = useRef<string>("");
  const startTimeRef = useRef<number>(Date.now());
  const didStartRef = useRef(false);

  const [pipelineLog, setPipelineLog] = useState<LogEntry[]>([]);

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishError, setPublishError] = useState("");

  const canPublish = role === "admin" || role === "editor";

  // Elapsed [MM:SS] from page load
  function elapsed(): string {
    const s = Math.floor((Date.now() - startTimeRef.current) / 1000);
    return `[${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}]`;
  }

  function buildExtractionLogs(log: any[]): LogEntry[] {
    const baseTsMs = log?.[0]?.ts ? new Date(log[0].ts).getTime() : null;
    return log.map((item: any) => {
      const s = baseTsMs && item.ts ? Math.max(0, Math.round((new Date(item.ts).getTime() - baseTsMs) / 1000)) : 0;
      return {
        time: `[${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}]`,
        status: item.status === "warning" ? ("warning" as LogStatus) : ("success" as LogStatus),
        message: item.message,
        detail: item.detail,
      };
    });
  }

  // On mount: call process/start (new flow) or init polling (legacy)
  // didStartRef guards against React 18 StrictMode double-fire which would create 2 reports
  useEffect(() => {
    if (didStartRef.current) return;
    didStartRef.current = true;
    if (state?.processPayload) {
      api
        .post("/api/process/start", state.processPayload)
        .then((res) => {
          const d = res.data;
          setReportId(d.report_id);
          setExtractionLogs(buildExtractionLogs(d.log ?? []));
          if (d.agent_run_id) {
            setAgentRunId(d.agent_run_id);
            setAgentStatus("running");
            setPipelineLog([{ time: elapsed(), status: "running", message: "Agent run queued" }]);
          }
        })
        .catch((e: any) => {
          setPrepareError(e?.response?.data?.detail || "Failed to start processing. Please go back and try again.");
          setAgentStatus("failed");
        })
        .finally(() => setPreparing(false));
    } else if (state?.agentRunId) {
      setAgentRunId(state.agentRunId);
      setAgentStatus("running");
      setPipelineLog([{ time: elapsed(), status: "running", message: "Agent run queued" }]);
      if (state.log) setExtractionLogs(buildExtractionLogs(state.log));
    }
  }, []);

  // Poll agent run status every 5 seconds
  useEffect(() => {
    if (!agentRunId || agentStatus === "complete" || agentStatus === "failed") return;
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const resp = await fetch(`${BASE}/api/agent/runs/${agentRunId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        const newStage = data.output_type || "";

        // Detect stage transition and append log entry
        if (newStage && newStage !== prevStageRef.current) {
          prevStageRef.current = newStage;
          setAgentStage(newStage);
          const label = STAGE_LABELS[newStage];
          if (label) {
            const isFail = newStage === "failed";
            setPipelineLog((prev) => {
              const updated = prev.map((e, i) =>
                i === prev.length - 1 && e.status === "running"
                  ? { ...e, status: isFail ? ("failed" as LogStatus) : ("success" as LogStatus) }
                  : e
              );
              return [
                ...updated,
                { time: elapsed(), status: isFail ? ("failed" as LogStatus) : ("running" as LogStatus), message: label },
              ];
            });
          }
        }

        if (data.status === "complete") {
          // Clear upload selections so a new report starts fresh
          const userId = localStorage.getItem("user_id") || "anon";
          sessionStorage.removeItem(`upload_${userId}_${projectId}`);
          setAgentStatus("complete");
          setProgress(100);
          setPipelineLog((prev) => {
            const updated = prev.map((e, i) =>
              i === prev.length - 1 ? { ...e, status: "success" as LogStatus } : e
            );
            return [...updated, { time: elapsed(), status: "success", message: "Report generation complete" }];
          });
          clearInterval(interval);
        } else if (data.status === "failed") {
          setAgentStatus("failed");
          setPipelineLog((prev) =>
            prev.map((e, i) =>
              i === prev.length - 1 ? { ...e, status: "failed" as LogStatus } : e
            )
          );
          clearInterval(interval);
        } else {
          setAgentStatus("running");
          setProgress((p) => Math.min(p + 2, 97));
        }
      } catch (e) {
        console.error("Agent poll error", e);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [agentRunId, agentStatus]);

  const handlePublish = async () => {
    if (!reportId) return;
    setPublishing(true);
    setPublishError("");
    try {
      await api.post(`/api/reports/${reportId}/publish`);
      setPublished(true);
    } catch (e: any) {
      setPublishError(e?.response?.data?.detail || "Failed to publish. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const isComplete = agentStatus === "complete";
  const isFailed = agentStatus === "failed" && !preparing;

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="report" />

      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">GENERATING</p>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {state?.reportTitle || state?.form?.report_title || "EAC Report"}
        </h1>

        {/* Progress bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium text-gray-700">
              {isComplete ? "Complete" : isFailed ? "Failed" : preparing ? "Preparing..." : "Processing..."}
            </p>
            <p className="text-sm font-medium text-gray-700">{progress}%</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${isFailed ? "bg-red-500" : "bg-blue-600"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {isComplete
              ? "Report ready for review"
              : isFailed
              ? "Agent failed — you can still view the report"
              : agentStage
              ? AGENT_STAGE_PROGRESS[agentStage] || "AI agent running..."
              : preparing ? "Parsing uploaded files..."
              : "Initialising AI agent..."}
          </p>
        </div>

        {/* Processing log */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <p className="text-sm font-semibold text-gray-900 mb-4">PROCESSING LOG</p>
          <div className="space-y-3">
            {/* Preparing state — while process/start is in flight */}
            {preparing && (
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Parsing files and extracting data...
              </div>
            )}

            {/* Prepare error */}
            {prepareError && (
              <p className="text-sm text-red-600">{prepareError}</p>
            )}

            {/* Data extraction steps — real data from backend */}
            {extractionLogs.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-xs text-blue-500 font-mono w-14 flex-shrink-0 mt-0.5">{item.time}</span>
                <StatusIcon status={item.status} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.message}</p>
                  {item.detail && <p className="text-xs text-gray-400 mt-0.5">· {item.detail}</p>}
                </div>
              </div>
            ))}

            {/* Divider between extraction and agent phases */}
            {extractionLogs.length > 0 && pipelineLog.length > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}

            {/* Agent pipeline stages — live-appended as polling detects transitions */}
            {pipelineLog.map((item, i) => (
              <div key={`pl-${i}`} className="flex items-start gap-3">
                <span className="text-xs text-blue-500 font-mono w-14 flex-shrink-0 mt-0.5">{item.time}</span>
                <StatusIcon status={item.status} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Complete / waiting card */}
        {isComplete || isFailed ? (
          <div
            className={`rounded-xl p-5 border ${
              isComplete ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"
            }`}
          >
            <p className={`text-sm font-semibold mb-1 ${isComplete ? "text-green-800" : "text-yellow-800"}`}>
              {isComplete ? "Report generation complete" : "Report ready (AI summary unavailable)"}
            </p>
            <p className={`text-xs mb-4 ${isComplete ? "text-green-600" : "text-yellow-600"}`}>
              Report ID {reportId || "—"} · Created{" "}
              {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} by {name}
            </p>

            {published && (
              <div className="flex items-center gap-2 bg-green-100 border border-green-300 rounded-lg px-3 py-2 mb-3">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-xs text-green-700 font-medium">
                  Report published — viewers can now access it from the dashboard.
                </p>
              </div>
            )}

            {publishError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                <p className="text-xs text-red-600">{publishError}</p>
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(reportId ? `/reports/${reportId}` : "/reports")}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Report
              </button>

              <button className="border border-gray-300 text-gray-700 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PowerPoint
              </button>

              {canPublish && !published && (
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="border border-green-500 text-green-700 bg-green-50 px-5 py-2 rounded-lg text-sm font-medium hover:bg-green-100 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {publishing ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Publish Report
                    </>
                  )}
                </button>
              )}

              {published && (
                <span className="flex items-center gap-1.5 text-xs text-green-700 font-medium px-3 py-2 bg-green-100 rounded-lg border border-green-200">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Published
                </span>
              )}

              <button
                onClick={() => navigate("/dashboard/" + projectId)}
                className="border border-gray-300 text-gray-700 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>

            {!canPublish && (
              <p className="text-xs text-gray-400 mt-3">
                Only Admin or Editor roles can publish reports. Contact your project manager to publish.
              </p>
            )}
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <p className="text-sm font-semibold text-blue-800 mb-1">AI agent is running...</p>
            <p className="text-xs text-blue-600">
              Generating narrative summaries, variance explanations, and AI insights. This may take 1–2 minutes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
